import { useState, type ReactNode } from 'react';
import {
  Package,
  Folder,
  FileText,
  ListChecks,
  LayoutGrid,
  SquareStack,
  PanelsTopLeft,
  Cable,
} from 'lucide-react';

/** Small inline icon that sits on the text baseline. */
function II({ icon }: { icon: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', verticalAlign: '-2px', marginRight: 3 }}>{icon}</span>
  );
}

/** Themed chip with a leading monochrome icon (replaces coloured emoji). */
function Chip({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span style={chipStyle}>
      {icon}
      {children}
    </span>
  );
}

interface Props {
  onClose: () => void;
  initialTab?: 'gettingStarted' | 'features' | 'shortcuts';
}

type Row = { keys: string[]; desc: string };
type Section = { title: string; rows: Row[] };

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

const SHORTCUT_SECTIONS: Section[] = [
  {
    title: 'History',
    rows: [
      { keys: [MOD, 'Z'], desc: 'Undo' },
      { keys: [MOD, 'Shift', 'Z'], desc: 'Redo' },
      { keys: [MOD, 'Y'], desc: 'Redo (alt)' },
    ],
  },
  {
    title: 'Editing',
    rows: [
      { keys: [MOD, 'C'], desc: 'Copy selected component' },
      { keys: [MOD, 'V'], desc: 'Paste at cursor position' },
      { keys: [MOD, 'D'], desc: 'Duplicate selected component' },
      { keys: ['Delete'], desc: 'Delete selected component or wire' },
      { keys: ['Backspace'], desc: 'Delete selected component or wire' },
      { keys: ['Esc'], desc: 'Cancel the active placement tool / clear harness or net focus' },
    ],
  },
  {
    title: 'Component',
    rows: [
      { keys: ['R'], desc: 'Rotate 90° clockwise' },
      { keys: ['Shift', 'R'], desc: 'Rotate 90° counter-clockwise' },
    ],
  },
  {
    title: 'Mouse',
    rows: [
      { keys: ['Drag'], desc: 'Move component on canvas' },
      { keys: ['Drag pin → pin'], desc: 'Connect a wire between two pins' },
      { keys: ['Drag on empty'], desc: 'Rectangle multi-select' },
      { keys: ['Shift + click'], desc: 'Add/remove from selection (works for wires too)' },
      { keys: ['Click wire'], desc: 'Select wire — both bend handles appear, endpoint pins highlight' },
      { keys: ['Click component'], desc: 'Select component AND highlight every wire connected to it' },
      { keys: ['Drag bend handle'], desc: 'Shift wire routing to avoid overlap (orthogonal)' },
      { keys: ['Double-click pin'], desc: 'Rename the pin inline' },
      { keys: ['Double-click note'], desc: 'Edit annotation text inline' },
      { keys: ['Scroll'], desc: 'Zoom the canvas' },
    ],
  },
  {
    title: 'Project tree',
    rows: [
      { keys: ['Click sheet'], desc: 'Switch to that sheet' },
      { keys: ['Double-click sheet'], desc: 'Rename inline' },
      { keys: ['Right-click root'], desc: 'New / Open / Save / Export menu' },
      { keys: ['Right-click sheet'], desc: 'Open in new window, export PNG/xlsx for that sheet' },
      { keys: ['Hover sheet'], desc: '× delete button appears (when more than one of that kind exists)' },
    ],
  },
  {
    title: 'Help',
    rows: [{ keys: ['?'], desc: 'Show / hide this panel' }],
  },
];

interface Feature {
  name: string;
  desc: string;
}

interface FeatureGroup {
  title: string;
  items: Feature[];
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    title: 'Canvas & editing',
    items: [
      { name: 'Drag-and-drop components', desc: 'Drop library items on the canvas to place them.' },
      { name: 'Pin-to-pin wiring', desc: 'Click a pin and drag to another pin to draw a wire.' },
      { name: 'Component rotation', desc: '90° steps with R / Shift+R. Pins relayout cleanly.' },
      { name: 'Copy / Paste / Duplicate', desc: 'Ctrl+C / V / D. New pin IDs so wires don’t collide.' },
      { name: 'Multi-select', desc: 'Shift-click or rectangle-drag on empty canvas. Bulk delete, alignment, harness assignment, gauge & colour.' },
      { name: 'Undo / Redo', desc: 'Ctrl+Z / Shift+Ctrl+Z. Rapid drags coalesce into one history entry.' },
      { name: 'Command toolbar', desc: 'Compact icon toolbar across the top — File, Edit, Arrange, View, Insert, Tools, Export. Hover for tooltips.' },
      { name: 'Component focus', desc: 'Click a component → its wires highlight, the rest fade to background.' },
      { name: 'Zoom, pan, minimap', desc: 'Scroll to zoom, drag empty space to pan, minimap bottom-right.' },
    ],
  },
  {
    title: 'Project structure (sheets)',
    items: [
      { name: 'Six sheet kinds', desc: 'Block diagram · Schematic · Mechanical layout · Requirements · Harnesses · Document.' },
      { name: 'Subsystems', desc: '+ → Subsystem creates Requirements + Block + Schematic + Mechanical sheets prefixed with the subsystem name. From the 2nd subsystem, the between-subsystems kit (System Overview + Harnesses + System Wiring) is added automatically.' },
      { name: 'System Overview & System Wiring', desc: 'A project-wide block sheet with one block per subsystem (draw harness lines between enclosures) plus a System Wiring schematic for the pin-to-pin links that cross between them.' },
      { name: 'Project tree dock', desc: 'Altium-style tree in the Projects tab of the left dock — group sheets by subsystem, click to navigate.' },
      { name: 'Right-click context menus', desc: 'Project root, subsystem groups, and individual sheets each have their own actions menu.' },
      { name: 'Pop-out sheet to new window', desc: 'Right-click a sheet → "Open in new window" — keep block diagram on one monitor while editing schematic on another. State syncs in real time.' },
      { name: 'Inline rename', desc: 'Double-click any sheet in the tree to edit its name in place.' },
    ],
  },
  {
    title: 'Component library',
    items: [
      { name: 'Custom components from scratch', desc: 'Define label, category, part number, manufacturer, color, default properties, pins.' },
      { name: 'Pin groups / connectors', desc: 'Group pins under a connector (e.g. "CN13 DC Out") — they render with a bracket and header.' },
      { name: 'Pin metadata', desc: 'Name, side, kind (power/gnd/signal/comm/analog/pwm/other), voltage, notes.' },
      { name: 'Prebuilt templates', desc: 'BLDC motor, SBLG2360 driver, AC/DC PSU, DC/DC, Relay, Contactor, Fuse, Breaker, Push Button, E-Stop, Limit Switch, I²C Sensor, Terminal Block.' },
      { name: 'Library persistence', desc: 'Saved in the browser (localStorage), shared across all projects.' },
      { name: 'Import / Export library', desc: 'JSON file — move your library between machines or back it up.' },
      { name: 'Live search', desc: 'Filter the palette by label, category, part number or manufacturer.' },
      { name: 'Rename connector in one click', desc: 'Inspector "Connectors" section renames a group and all its pins together.' },
    ],
  },
  {
    title: 'Nets & connectivity',
    items: [
      { name: 'Net = wire label', desc: 'A wire’s net name is its label — type "12V" or "RS232" on a wire and it becomes the net. Old projects light up automatically with no extra entry.' },
      { name: 'Nets panel (Tools ▸ Nets)', desc: 'Browse every electrical net derived from the wiring; click one to focus it on the canvas (the rest fade).' },
      { name: 'Connection list (Tools ▸ Conns)', desc: 'Editable table of every wire — number, From/To, net, gauge, colour, harness. Multi-select rows and bulk-assign a harness; auto-number wires (W1, W2…).' },
      { name: 'Standardize net names (Tools ▸ Std Net)', desc: 'One-click clean-up with preview: GND/0V → RTN, chassis → PE, voltage tidy. Voltage / ISO prefixes are kept so isolated returns (12V RTN vs 24V RTN) stay distinct nets.' },
      { name: 'Power-wire filter', desc: 'Toolbar toggle: show all wires, hide power, or show only power. Detects power by pin kind (power/gnd) and by label (L/N/PE, 12V RTN, 24V, VBAT…).' },
      { name: 'Rule checker (Tools ▸ Check)', desc: 'Net-aware ERC: flags net-name conflicts, single-pin (floating) nets, voltage mismatches; click an issue to jump to it.' },
      { name: 'Netlist export', desc: 'Export the derived netlist as CSV, plus a Nets worksheet in the Spec xlsx.' },
    ],
  },
  {
    title: 'Wires',
    items: [
      { name: 'Orthogonal routing', desc: 'Every wire is fully orthogonal (90° corners only). Two draggable bend handles appear when selected.' },
      { name: 'Wire number + net + gauge', desc: 'On-canvas label shows the wire number (W1, W2…), net/label and gauge. Labels sit above components and stagger along dense parallel runs so they don’t pile up.' },
      { name: 'Two-tone wires', desc: 'Give a wire a second colour for striped conductors (e.g. orange/white) — rendered as a dashed overlay.' },
      { name: 'Visibility halo', desc: 'Faint dark outline behind every wire so even white/very-light wires stay readable on the white sheet.' },
      { name: 'Wire properties', desc: 'Number, net name, AWG gauge, colour(s), notes — editable in the Inspector or the Connection list.' },
      { name: 'Colour rendering', desc: 'Colour names (Red, Blue, Brown, Green/Yellow PE, Turkish names…) + hex. Rendered on canvas.' },
      { name: 'Splices (≥8 way)', desc: 'Drop a splice / junction (Tools ▸ Splice or the left rail) to tie many wires into one node.' },
      { name: 'Voltage mismatch warning', desc: 'Different voltages on the two ends → red wire + ⚠ chip. Also flags power↔GND shorts.' },
      { name: 'Endpoint pin highlight', desc: 'Selecting a wire highlights both pin endpoints and shows the From/To breakdown in the Inspector.' },
      { name: 'Bulk multi-wire edit', desc: 'Select 2+ wires → Inspector shows bulk panel: assign harness, set gauge, set colour, or delete the lot in one click.' },
    ],
  },
  {
    title: 'Harness / production cabling',
    items: [
      { name: 'Harness assignment per wire', desc: 'Inspector dropdown picks the harness this wire physically belongs to. Multiple wires share a harness when bundled into the same loom.' },
      { name: 'Quick-create harness', desc: '“+ New harness…” inline in both the single-wire and multi-wire panels — name it, harness is created in the project’s Harness sheet, wire(s) auto-assigned.' },
      { name: 'Auto-filled metadata', desc: 'When you create a harness from selected wires, Skematiq seeds From / To unit and the “Cables in bundle” summary from the wires’ endpoints, gauge and colour.' },
      { name: 'Visual breakdown', desc: 'The Harness sheet shows each harness with a wire count, a Focus button, and an expandable list of all member wires grouped by connection pair.' },
      { name: 'Focus mode', desc: 'Focus a harness → canvas auto-jumps to the first schematic with its wires; those wires glow, the rest fade to the background.' },
      { name: 'Production xlsx export', desc: 'Wiring export adds a Harness column AND emits a dedicated worksheet per harness with banner, conduit, length, and full connector + pin breakdown — give it straight to assembly.' },
    ],
  },
  {
    title: 'Annotations',
    items: [
      { name: 'Sticky notes', desc: 'Place free-floating text on the canvas ("E-STOP LOOP", "TODO: add fuse"…). Add via the left tool rail (Note) or Insert ▸ Note.' },
      { name: 'Inline editing', desc: 'Double-click to edit; blur to save.' },
      { name: 'Custom background color', desc: 'Colour picker in the Inspector.' },
    ],
  },
  {
    title: 'Documents',
    items: [
      { name: 'Free-form text sheets', desc: 'Installation guide, maintenance manual, troubleshooting checklist — pick a category and add as many sections as you need.' },
      { name: 'Section reordering', desc: 'Up/down arrows on each section card; multi-line plain text bodies.' },
    ],
  },
  {
    title: 'Layout & alignment',
    items: [
      { name: 'Align 6 ways', desc: 'Left, right, top, bottom, H-center, V-center — any multi-selection of components.' },
      { name: 'Distribute evenly', desc: 'Horizontal or vertical equal spacing for 3+ selected components.' },
      { name: 'Grid snap', desc: 'Toggle 20 px snap so placements stay tidy.' },
    ],
  },
  {
    title: 'Project management',
    items: [
      { name: 'Autosave + crash recovery', desc: 'Debounced local snapshot; offers to resume on next launch via a React modal (no native confirm focus issues).' },
      { name: 'Save / Open .skema.json', desc: 'Portable single-file project with all sheets, components, harnesses, and documents embedded.' },
      { name: 'Project metadata', desc: 'Name, author, description — go into exports.' },
      { name: 'In-app crash recovery', desc: 'If a render error occurs, the ErrorBoundary lets you reload the window or discard the autosave instead of leaving you with a black screen.' },
      { name: 'Toast feedback', desc: 'Confirmation for save, export, errors, resume.' },
    ],
  },
  {
    title: 'Exports',
    items: [
      { name: 'PNG image', desc: 'Canvas snapshot at 2×, full diagram bounds (incl. wires + labels). From the project tree right-click on root or a specific sheet.' },
      { name: 'Wiring .xlsx', desc: 'Every wire as a row plus a Harness column. Plus one dedicated worksheet per harness for assembly.' },
      { name: 'Spec .xlsx', desc: 'Multiple sheets — Summary, Components, Pins, Wiring, Harnesses, Requirements, BOM — with filters and frozen headers.' },
      { name: 'BOM .xlsx', desc: 'Bill of materials — components aggregated by part, with quantities, manufacturer, specs.' },
      { name: 'Library JSON', desc: 'Export your custom component library for backup or sharing.' },
    ],
  },
  {
    title: 'UI & quality of life',
    items: [
      { name: 'Altium-style dark workstation', desc: 'A single dark UI around an always-white drawing sheet, with a blue command accent. Built for long editing sessions.' },
      { name: 'Thin menu + compact icon toolbar', desc: 'A classic menu bar (File / Edit / View / Help) over a single-row icon toolbar grouped by separators; hover any button for its tooltip.' },
      { name: 'Dockable side panels', desc: 'Projects and Inspector docks are width-resizable (drag the edge) and collapse to a thin rail; widths persist.' },
      { name: 'Bottom panel tabs', desc: 'The left dock splits into PROJECTS / LIBRARY tabs at the bottom — each opens standalone instead of stacking.' },
      { name: 'Left tool rail', desc: 'Context-aware vertical tools beside the canvas: Select, Note, and (per sheet) Splice or Block.' },
      { name: 'Drag-to-reorder sheets', desc: 'Inside the project tree, drag groups or individual sheets to reorder them.' },
      { name: 'Compact component nodes', desc: 'Pins auto-label, long group names auto-shrink, brackets nest toward pins.' },
      { name: 'Help panel', desc: 'This dialog — press ? anytime.' },
    ],
  },
];

export function HelpModal({ onClose, initialTab = 'gettingStarted' }: Props) {
  const [tab, setTab] = useState<'gettingStarted' | 'features' | 'shortcuts'>(initialTab);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Help</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                style={tabBtn(tab === 'gettingStarted')}
                onClick={() => setTab('gettingStarted')}
              >
                Getting started
              </button>
              <button
                style={tabBtn(tab === 'features')}
                onClick={() => setTab('features')}
              >
                Features
              </button>
              <button
                style={tabBtn(tab === 'shortcuts')}
                onClick={() => setTab('shortcuts')}
              >
                Shortcuts
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                fontFamily: 'ui-monospace, Consolas, monospace',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.6,
                color: 'var(--text-dim)',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Skematiq v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''}
            </span>
            <button style={iconBtn} onClick={onClose} title="Close (Esc)">
              ×
            </button>
          </div>
        </div>

        <div style={body}>
          {tab === 'gettingStarted' && <GettingStartedTab />}
          {tab === 'features' && <FeaturesTab />}
          {tab === 'shortcuts' && <ShortcutsTab />}
        </div>

        <div style={footer}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            Tip: shortcuts don't fire while typing in an input field.
          </div>
          <div style={{ flex: 1 }} />
          <button style={primaryBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Walk-through aimed at brand-new users: explains the project model
 * (subsystems → sheets) and the typical 5-minute first-project workflow.
 * Goal is "you don't have to remember the em-dash subsystem-naming
 * convention because Skematiq does it for you when you use the right buttons."
 */
function GettingStartedTab() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 760 }}>
      <div>
        <div style={sectionTitle}>How Skematiq is organised</div>
        <div style={featureRow}>
          <div style={{ ...featureName, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <II icon={<Package size={13} />} /> Project ▸ <II icon={<Folder size={13} />} /> Subsystems ▸{' '}
            <II icon={<FileText size={13} />} /> Sheets
          </div>
          <div style={featureDesc}>
            A <strong>project</strong> is your whole machine (e.g. "ICleaner"). Inside
            a project you create one <strong>subsystem</strong> per physical
            enclosure / module — typically <em>Robot</em>, <em>Control Box</em>,{' '}
            <em>Sensor Pod</em>, etc. Each subsystem groups four sheets:
            <br />
            <Chip icon={<ListChecks size={11} />}>Requirements</Chip>
            <Chip icon={<LayoutGrid size={11} />}>Block diagram</Chip>
            <Chip icon={<SquareStack size={11} />}>Schematic</Chip>
            <Chip icon={<PanelsTopLeft size={11} />}>Mechanical</Chip>
            <br />
            From the <strong>second subsystem on</strong>, Skematiq auto-adds the
            between-subsystems kit — a project-wide{' '}
            <Chip icon={<LayoutGrid size={11} />}>System Overview</Chip> (one block per
            subsystem, ready to draw harness lines between them), a{' '}
            <Chip icon={<Cable size={11} />}>Harnesses</Chip> sheet where every cable
            harness lives, and a{' '}
            <Chip icon={<SquareStack size={11} />}>System Wiring</Chip> schematic for the
            pin-to-pin links that cross between enclosures. Each subsystem-to-subsystem
            run is documented with the harness&apos;s <strong>From unit / To unit</strong> fields.
          </div>
        </div>
        <div style={{ ...featureRow, marginTop: 8 }}>
          <div style={featureName}>You don't have to type the dashes</div>
          <div style={featureDesc}>
            Skematiq groups sheets in the project tree by parsing names like{' '}
            <code>Robot — Schematic</code>. <strong>You don't have to write
            that yourself</strong> — the <strong>“+ → Subsystem”</strong> action
            in the project-tree header creates the whole set with the right
            name pattern. Just type a subsystem name (e.g. <code>Robot</code>),
            press Enter, and its four sheets show up under a new{' '}
            <II icon={<Folder size={12} />} />Robot folder.
          </div>
        </div>
      </div>

      <div>
        <div style={sectionTitle}>Your first project — 5 minute walkthrough</div>
        <Step n={1} title="Add your first subsystem">
          In the left dock’s <strong>Projects</strong> tab, in the{' '}
          <strong>PROJECT</strong> header click the blue <strong>+</strong>{' '}
          button → choose <strong>Subsystem</strong> → name it <code>Robot</code>{' '}
          → <em>Add subsystem</em>. A new <II icon={<Folder size={12} />} />Robot folder
          appears with its Requirements, Block, Schematic and Mechanical sheets.
        </Step>
        <Step n={2} title="Sketch the block diagram">
          Click <strong>Robot ▸ Block diagram</strong> — the big-picture view.
          Use <strong>Insert ▸ Block</strong> (or the left tool rail) to drop a
          block for each major module (driver, motor, PSU…), then drag between
          blocks to draw the high-level connections. You can even assign a
          harness to a block-to-block link here before drawing any detail.
        </Step>
        <Step n={3} title="Draw the schematic & drop components">
          Click <strong>Robot ▸ Schematic</strong>. Switch the left dock to its{' '}
          <strong>Library</strong> tab (bottom of the dock) and drag e.g. an{' '}
          <em>SBLG2360 Motor Driver</em> and a <em>BLDC Motor</em> onto the canvas.
        </Step>
        <Step n={4} title="Wire pin to pin, then name the net">
          Click a pin on one component and drag to a pin on another to draw a
          wire (fully orthogonal — drag its bend handles to keep things tidy).
          Select the wire and, in the Inspector, type its net name / label
          (e.g. <code>12V</code>), gauge and colour — the label <em>is</em> the
          net.
        </Step>
        <Step n={5} title="Add another subsystem — the system kit appears">
          Repeat step 1 with <code>Control Box</code>. The moment you have a
          second subsystem, Skematiq auto-adds the between-subsystems kit:{' '}
          <strong>System Overview</strong> (a block per subsystem — draw harness
          lines between them), the project <strong>Harnesses</strong> sheet, and
          a project-wide <strong>System Wiring</strong> schematic for pin-to-pin
          links that cross enclosures. Build out the new subsystem the same way.
        </Step>
        <Step n={6} title="Boundary interface — terminal or connector">
          Where a cable crosses between two enclosures, model the interface on
          each side as a normal component: a <strong>terminal block</strong>{' '}
          (e.g. <code>X1</code> — a screw/spring strip the cable lands on) or a{' '}
          <strong>connector</strong> (a mating plug/socket). Both are just
          library parts with pins — pick whichever matches your real hardware.
          The <code>X1 / X2 / J1</code> designators and the bracket around
          grouped pins are <em>naming/grouping</em>, not a fixed “this is a
          connector” marker. The example project uses <strong>connectors</strong>{' '}
          (<code>X1</code>/<code>X2</code>) at the enclosure boundary; swap in a
          terminal-block part instead if your machine lands the cable on a
          screw/spring strip.
        </Step>
        <Step n={7} title="Define cable harnesses">
          Switch to the <strong>Harnesses</strong> sheet and click{' '}
          <em>Add harness page</em> to create a harness (a connector/pinout
          table page, auto-filled from the schematic). Or skip this — you can
          create a harness straight from selected wires in the next step.
        </Step>
        <Step n={8} title="Group wires into the harness">
          Switch back to a schematic. <strong>Shift+click</strong> (or
          rectangle-drag) the wires that physically run together in one cable
          → Inspector shows the bulk panel → choose <strong>“+ New harness
          from selection…”</strong>. Skematiq pre-fills From/To and a cables
          list from those wires.
        </Step>
        <Step n={9} title="Visually check with Focus mode">
          Open the Harness sheet, expand the harness card, click{' '}
          <strong>Focus</strong>. Skematiq jumps to the schematic and lights its
          wires while the rest fade out. Spot mistakes at a glance.
        </Step>
        <Step n={10} title="Export for production">
          Right-click the project root in the tree → <strong>Export Wiring
          (.xlsx)</strong>. You get a master wiring sheet plus one
          production-ready worksheet per harness — ready to hand to assembly.
        </Step>
      </div>

      <div>
        <div style={sectionTitle}>Useful tricks</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text)', fontSize: 12, lineHeight: 1.6 }}>
          <li>
            <strong>Click a component</strong> → all wires connected to it
            light up, the rest fade.
          </li>
          <li>
            <strong>Right-click a sheet</strong> → "Open in new window" puts
            that sheet on a second monitor, synced live.
          </li>
          <li>
            <strong>+ → Document</strong> creates a free-form text sheet —
            installation guides, maintenance manuals, troubleshooting.
          </li>
          <li>
            <strong>Tools ▸ Conns</strong> opens the Connection list — multi-select
            wires and bulk-assign a harness, auto-number them, or edit net /
            gauge / colour in one table.
          </li>
          <li>
            <strong>Tools ▸ Std Net</strong> standardizes net names (GND/0V → RTN,
            chassis → PE) with a preview, and the toolbar <strong>power filter</strong>{' '}
            hides or isolates power wires.
          </li>
          <li>
            <strong>Dock panels</strong>: drag a side panel’s edge to resize it,
            and switch the left dock between <em>Projects</em> and <em>Library</em>{' '}
            from the tabs at its bottom.
          </li>
        </ul>
      </div>
    </div>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr',
        gap: 10,
        padding: '8px 0',
        borderBottom: '1px solid var(--panel-border)',
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          fontSize: 11,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        {n}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>{title}</div>
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--text-muted)',
            lineHeight: 1.55,
            marginTop: 2,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 10,
  fontWeight: 600,
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 999,
  padding: '2px 8px',
  margin: '4px 4px 0 0',
  color: 'var(--text)',
};

function FeaturesTab() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20,
      }}
    >
      {FEATURE_GROUPS.map((group) => (
        <div key={group.title}>
          <div style={sectionTitle}>{group.title}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.items.map((f) => (
              <div key={f.name} style={featureRow}>
                <div style={featureName}>{f.name}</div>
                <div style={featureDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ShortcutsTab() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 18,
      }}
    >
      {SHORTCUT_SECTIONS.map((section) => (
        <div key={section.title}>
          <div style={sectionTitle}>{section.title}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {section.rows.map((row, i) => (
              <div key={i} style={rowStyle}>
                <div style={keysCell}>
                  {row.keys.map((k, j) => (
                    <span key={j} style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <kbd style={kbdStyle}>{k}</kbd>
                      {j < row.keys.length - 1 && (
                        <span style={{ margin: '0 4px', color: 'var(--text-dim)' }}>+</span>
                      )}
                    </span>
                  ))}
                </div>
                <div style={descCell}>{row.desc}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.35)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modal: React.CSSProperties = {
  width: 880,
  maxWidth: '95vw',
  maxHeight: '88vh',
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column',
};

const header: React.CSSProperties = {
  padding: '14px 18px',
  borderBottom: '1px solid var(--panel-border)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const body: React.CSSProperties = {
  padding: 18,
  overflowY: 'auto',
  flex: 1,
};

const footer: React.CSSProperties = {
  padding: '12px 18px',
  borderTop: '1px solid var(--panel-border)',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  background: 'var(--panel-alt)',
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  paddingBottom: 8,
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(130px, auto) 1fr',
  gap: 10,
  alignItems: 'center',
  padding: '3px 0',
  fontSize: 12,
};

const keysCell: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  alignItems: 'center',
};

const descCell: React.CSSProperties = {
  color: 'var(--text)',
  lineHeight: 1.4,
};

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 7px',
  background: 'var(--bg-canvas)',
  border: '1px solid var(--panel-border-strong)',
  borderBottomWidth: 2,
  borderRadius: 5,
  fontSize: 11,
  fontFamily: 'ui-monospace, Consolas, monospace',
  fontWeight: 600,
  color: 'var(--text)',
  minWidth: 18,
  textAlign: 'center',
  lineHeight: 1.3,
};

const featureRow: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--panel-alt)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
};

const featureName: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 2,
};

const featureDesc: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  lineHeight: 1.45,
};

const iconBtn: React.CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  width: 28,
  height: 28,
  fontSize: 14,
  padding: 0,
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '7px 16px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.25)',
};

function tabBtn(active: boolean): React.CSSProperties {
  return {
    background: active ? 'var(--accent-soft)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
    border: '1px solid',
    borderColor: active ? '#bfdbfe' : 'transparent',
    borderRadius: 'var(--radius-sm)',
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };
}
